import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead from 'Components/EnhancedTableHead'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import type { ParameterGroup } from 'Services/ProductManagement/Features/Parameter/types'
import type {
  FetchProductListParams,
  Product
} from 'Services/ProductManagement/Features/Product/types'
import { ROWS_PER_PAGE_DEFAULT, SORT_ORDER_ASC } from 'Util/constants'
import {
  datetimeFormatter,
  getValueFromOptions,
  isoDurationToHumanReadableFormat,
  showValueOrHyphen
} from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import useRowSelect from 'Util/hooks/useRowSelect'
import { Checkbox, Paper, Table, TableBody, TableCell, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface ProductListProps {
  rows: Product[]
  isLoading: boolean
  params: FetchProductListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  allowSelect?: boolean
  customerGroupsParameter: ParameterGroup | null
  selectedProductId: string | undefined
  onRowClick: (id: string) => void
}

function ProductList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  allowSelect = false,
  page,
  customerGroupsParameter,
  selectedProductId,
  onRowClick
}: Readonly<ProductListProps>) {
  const { selected, onSelect, onSelectAll } = useRowSelect()

  const customerGroupOptions =
    customerGroupsParameter?.parameters.map(({ translations, parameter_id }) => ({
      label: getTranslation(translations, 'fi').value ?? '',
      value: parameter_id
    })) ?? []

  const headers: HeadCell[] = [
    {
      id: 'productCode',
      numeric: false,
      disablePadding: false,
      label: t`Product code`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Product name`,
      sortable: false
    },
    {
      id: 'customerGroup',
      numeric: false,
      disablePadding: false,
      label: t`Customer group`,
      sortable: true
    },
    {
      id: 'validityLengthSeconds',
      numeric: false,
      disablePadding: false,
      label: t`Valid`,
      sortable: true
    },
    {
      id: 'createdTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: true
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  return (
    <WithLoader rows={ROWS_PER_PAGE_DEFAULT} loading={isLoading} height={33}>
      <Paper sx={{ width: '100%' }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headers}
              onRequestSort={handleRequestSort}
              selectChecked={selected.length === rows.length}
              onSelectAllClick={
                allowSelect
                  ? (_, checked) =>
                      onSelectAll(
                        rows.map(row => row.id),
                        checked
                      )
                  : undefined
              }
            />
            <TableBody>
              {rows.map(row => {
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={selectedProductId === row.id}
                  >
                    {allowSelect && (
                      <TableCell sx={{ width: 30 }}>
                        <Checkbox
                          size="small"
                          sx={{ p: 0 }}
                          checked={selected.includes(row.id)}
                          onChange={(_, checked) => onSelect(checked, row.id)}
                        />
                      </TableCell>
                    )}
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {showValueOrHyphen(row.product_code)}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.id)}>
                      {getTranslation(row.translations, 'fi').name}
                    </ClickableTableCell>
                    <NoWrapTableCell>
                      {getValueFromOptions(row.customer_group, customerGroupOptions, '-')}
                    </NoWrapTableCell>
                    <NoWrapTableCell>
                      {isoDurationToHumanReadableFormat(row.usage_validity_duration)}
                    </NoWrapTableCell>
                    <NoWrapTableCell title={datetimeFormatter(new Date(row.created_timestamp))}>
                      {datetimeFormatter(new Date(row.created_timestamp))}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}

export default ProductList
